// ── Program detail ("/programs/:id") — the program record (Wave 2) ──────────
// The full program record: an identity header (name + status + company + coach)
// over the assigned-cohorts READ section. The header read routes through
// useHonestQuery -> <HonestState>; a gate / permission-denied / unavailable /
// error NEVER reads as an empty program shell, and a not-found (the 400 "was not
// found" faked 404 -> "empty" posture) renders an honest "Program not found",
// NEVER a cross-record leak.
//
// THE ID IS A STRING — the route param is the bigint-safe program id, passed
// VERBATIM (never Number()'d). A bad/absent id can't be a real record, so the
// page sends it back to the list rather than firing a doomed read.
//
// CONTRACT CHECK: there is NO program->cohort assignment write endpoint (the
// backend exposes only programs.list/create/detail/update). Assignment is set
// cohort-side at cohort create/edit (Wave 3). So this page surfaces the assigned
// cohorts as a READ (ProgramCohortsSection) with a clear note — it does NOT
// invent an assign flow.
//
// ROLE-GATING: Super Admin / Admin can Edit + Publish (draft) + Archive from the
// header; Coach / Company Admin are read-only (no write affordances); denied
// roles — incl. PARTICIPANT and cohort_lead — never reach the route (the route
// manifest's program-detail uses operationsRoles), and a denied READ renders
// permission-denied.

import { Badge, Button, DetailSurface, humanizeLabel, useToast } from "@vjal/ui";
import { useState } from "react";
import { Link } from "react-router-dom";

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { destructiveButtonClass } from "../../components/ui-classes";
import { getCoachingServices } from "../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../hooks/coaching-query-keys";
import { useOrganizationLifecycleMutations } from "../../hooks/useOrganizationLifecycleMutations";
import { asLiveApiId } from "../../hooks/useLiveProductData";
import { evaluateActionCapability } from "../../domain/capabilities";
import { liveRecordIdFromPath } from "../product-live-rows";
import type { CoachingProgramDto } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { ProgramFormModal, type ProgramFormValues } from "./program-form-modal";
import { ProgramLifecycleModal, type ProgramLifecycleAction } from "./program-lifecycle-modal";
import { ProgramCohortsSection } from "./program-cohorts-section";
import { programDisplayName, programStatusLabel, programStatusTone, isValidProgramId } from "./program-detail-helpers";

const services = getCoachingServices();

export function ProgramDetailPage({ role, routePath }: ProductScreenContext) {
  const toast = useToast();
  const mutations = useOrganizationLifecycleMutations();
  const allowUpdate = evaluateActionCapability(role, "programs:update").allowed;

  const programId = liveRecordIdFromPath(routePath, "programs");
  const validId = isValidProgramId(programId);

  const [editOpen, setEditOpen] = useState(false);
  const [lifecycle, setLifecycle] = useState<ProgramLifecycleAction | null>(null);

  const query = useHonestQuery<CoachingProgramDto>(
    coachingQueryKeys.programDetail(programId ?? ""),
    () => services.programs.detail(programId as string),
    { enabled: validId }
  );
  const program = query.data ?? null;

  function submitEdit(values: ProgramFormValues) {
    if (!program) return Promise.reject(new Error("No program loaded"));
    const body: Record<string, unknown> = { name: values.name, status: values.status };
    if (values.companyId) body.company_id = asLiveApiId(values.companyId);
    if (values.coachId) body.coach_user_id = asLiveApiId(values.coachId);
    return mutations.updateProgram
      .mutateAsync({ programId: program.id, body })
      .then((updated) => {
        setEditOpen(false);
        mutations.updateProgram.reset();
        toast.success(`Program ${updated.name ?? values.name} updated`);
        return updated;
      });
  }

  function submitLifecycle() {
    if (!program || !lifecycle) return Promise.reject(new Error("No program loaded"));
    const status = lifecycle === "publish" ? "active" : "archived";
    const action = lifecycle;
    return mutations.updateProgram
      .mutateAsync({ programId: program.id, body: { name: program.name, status } })
      .then((updated) => {
        setLifecycle(null);
        mutations.updateProgram.reset();
        toast.success(`Program ${updated.name ?? program.name} ${action === "publish" ? "published" : "archived"}`);
        return updated;
      });
  }

  // ── Bad/absent id, or a not-found / non-ready posture → one honest body. ────
  if (!validId || query.posture !== null || !program) {
    const posture = !validId ? "empty" : (query.posture ?? "loading");
    const notFound = posture === "empty";
    return (
      <section aria-label="Program detail" className="col-span-12" data-testid="program-detail-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture={posture}
            gate={query.gate}
            reference={query.reference}
            onRetry={query.retry}
            title={notFound ? "Program not found" : undefined}
            message={
              notFound
                ? "This program doesn't exist, or it isn't in your workspace. It may have been removed."
                : undefined
            }
            canCreate={notFound}
            createAction={
              notFound ? (
                <Link to="/programs" className="text-link no-underline hover:underline">
                  Back to programs
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>
    );
  }

  // ── Ready: render the program record ────────────────────────────────────────
  const archived = program.status === "archived";
  const isDraft = program.status === "draft";
  return (
    <section aria-label="Program detail" className="col-span-12 flex flex-col gap-4" data-testid="program-detail-page">
      {/* F3: real @vjal/ui DetailSurface for Detail-1 program-detail. */}
      <DetailSurface
        className="min-w-0 max-w-full [&>div>div>div:first-child]:flex-wrap [&>div>div>div:first-child]:gap-y-2 [&_h2]:min-w-0"
        title={
          <span className="flex min-w-0 max-w-full flex-wrap items-center gap-2 break-words sm:gap-3">
            {programDisplayName(program.name)}
            <Badge variant={programStatusTone(program.status)}>{programStatusLabel(program.status)}</Badge>
          </span>
        }
        actions={
          allowUpdate ? (
            <div
              className="flex w-full min-w-0 max-w-full flex-wrap justify-end gap-2 sm:w-auto sm:flex-1"
              data-testid="program-detail-actions"
              role="group"
              aria-label="Program actions"
            >
              <Button variant="secondary" onClick={() => setEditOpen(true)} aria-label="Edit program">
                Edit program
              </Button>
              {isDraft && (
                <Button variant="primary" onClick={() => setLifecycle("publish")} aria-label="Open publish confirmation">
                  Publish
                </Button>
              )}
              <Button
                variant="danger"
                className={destructiveButtonClass}
                onClick={() => setLifecycle("archive")}
                disabled={archived}
                aria-label="Open archive confirmation"
              >
                Archive
              </Button>
            </div>
          ) : undefined
        }
        fields={[
          {
            id: "back",
            label: "Directory",
            value: (
              <Link to="/programs" className="text-link no-underline hover:underline">
                ← All programs
              </Link>
            ),
          },
          {
            id: "reference",
            label: "Reference",
            value: program.id,
            mono: true,
          },
          {
            id: "company",
            label: "Company",
            value: program.company_name?.trim() ? program.company_name : "—",
          },
          {
            id: "coach",
            label: "Coach",
            value: program.coach_name?.trim() ? program.coach_name : "—",
          },
          {
            id: "lifecycle",
            label: "Lifecycle",
            value: humanizeLabel(program.status ?? "draft"),
          },
        ]}
        titleExactName="Program title">
        <div className="mt-4">
          <ProgramCohortsSection programId={program.id} />
        </div>
      </DetailSurface>

      {allowUpdate && (
        <>
          <ProgramFormModal
            open={editOpen}
            mode="edit"
            program={program}
            onClose={() => {
              setEditOpen(false);
              mutations.updateProgram.reset();
            }}
            onSubmit={submitEdit}
            submitting={mutations.updateProgram.isPending}
            submitError={mutations.updateProgram.error}
          />
          <ProgramLifecycleModal
            open={lifecycle !== null}
            action={lifecycle ?? "archive"}
            program={program}
            onClose={() => {
              setLifecycle(null);
              mutations.updateProgram.reset();
            }}
            onConfirm={submitLifecycle}
            submitting={mutations.updateProgram.isPending}
            submitError={mutations.updateProgram.error}
          />
        </>
      )}
    </section>
  );
}
